import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faChevronLeft, faChevronRight, faSun, faMoon, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import './Initiatives.css';

const CustomSunIcon = ({ className, style }) => (
  <svg 
    className={className}
    style={style}
    width="1.6rem" 
    height="1.6rem" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    {/* Central sun circle */}
    <circle cx="12" cy="12" r="5" fill="currentColor"/>
    
    {/* Sun rays */}
    <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {/* Vertical and horizontal rays */}
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </g>
  </svg>
);

const InitiativesPage = () => {
  const [currentInitiative, setCurrentInitiative] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const initiatives = [
    {
      title: " Forest Restoration Project",
      description: "In 2024, The Sustainable Formula took a significant step towards forest restoration by planting 50+ trees. This initiative was part of our ongoing efforts to contribute to reforestation and improve the health of local ecosystems.",
      images: [
        "/images/tree1.jpeg",
        "/images/tree2.jpeg",
        "/images/tree3.jpeg",
        "/images/tree7.jpeg"
      ],
      date: "2024",
      impact: "Aided in plantation of over 1000+ trees • 10+ volunteers"
    },
    {
      title: " Green Roof Initiative",
      description: "One of our upcoming projects is the Green Roof, aimed at creating sustainable urban spaces that help combat climate change. By transforming rooftops into green, plant-filled spaces, we plan to reduce urban heat, improve air quality, and provide more green areas in the city.",
      images: [
        "/images/comingsoon.jpeg"
      ],
      date: "To Be Decided",
      impact: ""
    },
    {
      title: " Rain Water Harvesting Project",
      description: "We aim to raise funds to install rain water harvesting systems for those in need, such as government schools, to reuse rain water to reduce the costs of operation for these schools so more funds towards educating their students.",
      images: [
        "/images/comingsoon.jpeg"
      ],
      date: "To Be Decided",
      impact: ""
    },
    {
      title: " Research Project",
      description: "We aim to conduct a survey on the extent of eutrophication in natural water bodies in Bengaluru. ",
      images: [
        "/images/comingsoon.jpeg"
      ],
      date: "To Be Decided",
      impact: ""
    },
    {
      title: "  YouthforGrowth",
      description: "Enabling underprivileged children to self-publish their own books.",
      images: [
        "/images/comingsoon.jpeg"
      ],
      date: "To Be Decided",
      impact: ""
    }
  ];

  const [currentImages, setCurrentImages] = useState(initiatives.map(() => 0));

  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';

    const imageInterval = setInterval(() => {
      setCurrentImages(prevImages =>
        prevImages.map((current, idx) =>
          (current + 1) % initiatives[idx].images.length
        )
      );
    }, 4500);

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });

      if (cursorRef.current && windowWidth > 768) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }

      if (cursorDotRef.current && windowWidth > 768) {
        cursorDotRef.current.style.left = `${e.clientX}px`;
        cursorDotRef.current.style.top = `${e.clientY}px`;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      clearInterval(imageInterval);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDarkMode, initiatives, windowWidth]);

  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);

  useEffect(() => {
    const createParticles = () => {
      const particleCount = windowWidth > 768 ? 50 : 20; // Fewer particles on mobile
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.width = `${Math.random() * (windowWidth > 768 ? 5 : 3)}px`; // Smaller particles on mobile
        particle.style.height = particle.style.width;
        particle.style.left = `${Math.random() * 100}vw`;
        particle.style.top = `${Math.random() * 100}vh`;
        document.body.appendChild(particle);
      }
    };

    createParticles();

    return () => {
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());
    };
  }, [isDarkMode, windowWidth]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const nextInitiative = () => {
    setCurrentInitiative((prev) => (prev + 1) % initiatives.length);
  };

  const prevInitiative = () => {
    setCurrentInitiative((prev) => (prev - 1 + initiatives.length) % initiatives.length);
  };

  const themeClass = isDarkMode ? 'dark-theme' : 'light-theme';

  const styles = {
    hamburger: {
      display: windowWidth <= 768 ? 'block' : 'none',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
    },
    mobileMenu: {
      display: mobileMenuOpen ? 'flex' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      zIndex: 1000,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    mobileMenuClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
    },
    mobileNavLinks: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      fontSize: '1.5rem',
    },
    mobileNavLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
      padding: '10px',
    },
    desktopNav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: windowWidth <= 768 ? '10px' : '20px',
      position: 'relative',
      zIndex: 100,
      flexWrap: 'nowrap',
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      fontSize: windowWidth <= 480 ? '0.89rem' : 'clamp(1rem, 3vw, 1.5rem)',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: windowWidth <= 480 ? '200px' : 'none',
    },
    logoText: {
      display: 'inline',
      fontSize: windowWidth <= 360 ? '0.8rem' : 'inherit',
    },
    logoIcon: {
      marginRight: '8px',
      display: 'inline-block',
    },
    navControls: {
      display: 'flex',
      alignItems: 'center',
      gap: windowWidth <= 480 ? '5px' : '10px',
      flexShrink: 0,
    },
    desktopNavLinks: {
      display: windowWidth <= 768 ? 'none' : 'flex',
      gap: '20px',
    },
    navLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
    },
    themeToggle: {
      background: 'none',
      border: 'none',
      fontSize: '1.2rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
      marginRight: '5px',
    }
  };

  return (
    <div className={`initiatives-container ${themeClass}`}>
      <div className={themeClass}>
        {windowWidth > 768 && (
          <>
            <div ref={cursorRef} className="custom-cursor" />
            <div ref={cursorDotRef} className="cursor-dot" />
          </>
        )}

        <div className="bg-overlay" />

        {/* Desktop Navigation */}
        <nav style={styles.desktopNav}>
          <Link to="/" style={styles.logoSection}>
            <FontAwesomeIcon icon={faLeaf} style={styles.logoIcon} />
            <span style={styles.logoText}>The Sustainable Formula</span>
          </Link>

          <div style={styles.navControls}>
            <div style={styles.desktopNavLinks}>
              <Link to="/team" style={styles.navLink}>Our Team</Link>
              <Link to="/about" style={styles.navLink}>About Us</Link>
              <Link to="/initiatives" style={styles.navLink}>Initiatives</Link>
              <Link to="/donate" style={styles.navLink}>Donate</Link>
            </div>

            <button 
              style={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <CustomSunIcon />
              ) : (
                <FontAwesomeIcon icon={faMoon} />
              )}
            </button>

            <button
              style={styles.hamburger}
              onClick={toggleMobileMenu}
              aria-label="Menu"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <div style={styles.mobileMenu}>
          <button
            style={styles.mobileMenuClose}
            onClick={toggleMobileMenu}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div style={styles.mobileNavLinks}>
            <Link
              to="/team"
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Our Team
            </Link>
            <Link
              to="/about"
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            <Link
              to="/initiatives"
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Initiatives
            </Link>
            <Link
              to="/donate"
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Donate
            </Link>
          </div>
        </div>

        <div className="initiatives-header">
          <h1>Our Initiatives</h1>
          <p>Making a difference through sustainable action</p>
        </div>

        <div className="initiative-card">
          {windowWidth > 768 ? (
            // Desktop view
            <>
              <button
                onClick={prevInitiative}
                className="nav-button prev-button"
                aria-label="Previous initiative"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className="grid">
                <div className="initiative-images">
                  <div className="image-container">
                    <img
                      src={initiatives[currentInitiative].images[currentImages[currentInitiative]]}
                      alt={initiatives[currentInitiative].title}
                      className="initiative-image"
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                  </div>
                  <div className="image-counter">
                    {currentImages[currentInitiative] + 1} / {initiatives[currentInitiative].images.length}
                  </div>
                </div>

                <div className="initiative-content">
                  <h2 className="initiative-title">
                    <FontAwesomeIcon icon={faLeaf} className="mr-2" />
                    {initiatives[currentInitiative].title}
                  </h2>
                  <p className="initiative-description">{initiatives[currentInitiative].description}</p>
                  <div className="text-sm font-medium mb-4">{initiatives[currentInitiative].date}</div>

                  <div className="impact-metrics">
                    <h3 className="impact-title">Impact Metrics</h3>
                    <p>{initiatives[currentInitiative].impact}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={nextInitiative}
                className="nav-button next-button"
                aria-label="Next initiative"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
          ) : windowWidth > 425 ? (
            // Tablet view (426px-768px)
            <div className="tablet-layout">
              <FontAwesomeIcon 
                icon={faChevronLeft} 
                onClick={prevInitiative}
                className="tablet-nav-arrow"
              />
              
              <div className="grid">
                <div className="initiative-images">
                  <div className="image-container">
                    <img
                      src={initiatives[currentInitiative].images[currentImages[currentInitiative]]}
                      alt={initiatives[currentInitiative].title}
                      className="initiative-image"
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                  </div>
                  <div className="image-counter">
                    {currentImages[currentInitiative] + 1} / {initiatives[currentInitiative].images.length}
                  </div>
                </div>

                <div className="initiative-content">
                  <h2 className="initiative-title">
                    <FontAwesomeIcon icon={faLeaf} className="mr-2" />
                    {initiatives[currentInitiative].title}
                  </h2>
                  <p className="initiative-description">{initiatives[currentInitiative].description}</p>
                  <div className="text-sm font-medium mb-4">{initiatives[currentInitiative].date}</div>

                  <div className="impact-metrics">
                    <h3 className="impact-title">Impact Metrics</h3>
                    <p>{initiatives[currentInitiative].impact}</p>
                  </div>
                </div>
              </div>

              <FontAwesomeIcon 
                icon={faChevronRight} 
                onClick={nextInitiative}
                className="tablet-nav-arrow"
              />
            </div>
          ) : (
            // Mobile view (320px-425px)
            <>
              <div className="grid">
                <div className="mobile-title-nav">
                  <FontAwesomeIcon 
                    icon={faChevronLeft} 
                    onClick={prevInitiative}
                    className="mobile-nav-arrow"
                  />
                  <h2 className="initiative-title">
                    <FontAwesomeIcon icon={faLeaf} className="mr-2" />
                    {initiatives[currentInitiative].title}
                  </h2>
                  <FontAwesomeIcon 
                    icon={faChevronRight} 
                    onClick={nextInitiative}
                    className="mobile-nav-arrow"
                  />
                </div>
                <div className="initiative-images">
                  <div className="image-container">
                    <img
                      src={initiatives[currentInitiative].images[currentImages[currentInitiative]]}
                      alt={initiatives[currentInitiative].title}
                      className="initiative-image"
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                  </div>
                  <div className="image-counter">
                    {currentImages[currentInitiative] + 1} / {initiatives[currentInitiative].images.length}
                  </div>
                </div>

                <div className="initiative-content">
                  <p className="initiative-description">{initiatives[currentInitiative].description}</p>
                  <div className="text-sm font-medium mb-4">{initiatives[currentInitiative].date}</div>

                  <div className="impact-metrics">
                    <h3 className="impact-title">Impact Metrics</h3>
                    <p>{initiatives[currentInitiative].impact}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="navigation-dots">
          {initiatives.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentInitiative(idx)}
              className={`dot ${currentInitiative === idx ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default InitiativesPage;